import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Grid,
  IconButton,
  TextField,
  Typography,
  Skeleton,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { getRequest } from "../../globalhelper/helper";
import PieChartOutlineIcon from "@mui/icons-material/PieChartOutline";
import {
  BarChart,
  Legend,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Bar,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import OpenInFullIcon from '@mui/icons-material/OpenInFull';

import { useSelector, useDispatch } from "react-redux";
import { setProjectData } from "../../redux/Dashboard/dashboardActions";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

const ProjectDashboard = ({ open, setOpen, company }) => {
  const [PeIDs, setPEIds] = useState([]);
  const [peId, setPeid] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { project } = useSelector((state) => state.dashboard);
  const { reports } = project;

  useEffect(() => {
    if (!company?.orgid) return;
    let isMounted = true;
    getRequest(`api/dashboard/getIP2?orgid=${company?.orgid}`)
      .then(({ res }) => {
        if (!isMounted) return;
        const parsed = JSON.parse(res.data || "[]");
        const sorted = parsed.sort((a, b) => {
          const aVal = a?.["IP Number"] ?? "";
          const bVal = b?.["IP Number"] ?? "";
          return aVal.localeCompare(bVal);
        });
        setPEIds(sorted);
      })
      .catch((error) => console.log(error));
    return () => {
      isMounted = false;
    };
  }, [company]);

  useEffect(() => {
    if (!company?.orgid) {
      return;
    }
    let isMounted = true;

    const formattedFromDate = fromDate ? fromDate.format("DD-MM-YYYY") : "";
    const formattedToDate = toDate ? toDate.format("DD-MM-YYYY") : "";

    setLoading(true);
    getRequest(`api/dashboard/getBudgetVsActuals?orgid=${company.orgid}&peid=${peId?.["IP Number"] || ""}&fromDate=${formattedFromDate}&toDate=${formattedToDate}`)
      .then(({ res }) => {
        if (!isMounted) return;
        const parsed = JSON.parse(res.data || "[]").map((item) => ({
          name: item.planningelementname,
          Budget: parseFloat(item.basevalue) || 0,
          Actuals: parseFloat(item.totalgsevalue) || 0,
        }));
        dispatch(setProjectData(parsed));
      })
      .catch((error) => console.log(error))
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [company, peId, fromDate, toDate]);



  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            bgcolor: "rgba(255, 255, 255, 0.94)",
            p: 1.2,
            border: "1px solid #f1f5f9",
            borderRadius: 2,
            boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
            backdropFilter: "blur(4px)",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.8, color: "#1e293b", fontSize: "0.8rem" }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.3 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: entry.fill }} />
              <Typography variant="caption" sx={{ color: "#475569", fontWeight: 500, fontSize: "0.7rem" }}>
                {entry.name}:
              </Typography>
              <Typography variant="caption" sx={{ color: "#1e293b", fontWeight: 700, fontSize: "0.7rem" }}>
                {typeof entry.value === 'number'
                  ? (entry.value >= 10000000
                    ? `${(entry.value / 10000000).toLocaleString('en-IN', { maximumFractionDigits: 2 })} Cr`
                    : entry.value >= 1000
                      ? `${(entry.value / 1000).toFixed(1)}k`
                      : entry.value.toLocaleString("en-IN"))
                  : entry.value}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
    return null;
  };

  const CustomizedAxisTick = (props) => {
    const { x, y, payload } = props;
    const text = payload.value;

    if (!open) {
      const displayedText = text.length > 15 ? text.slice(0, 15) + "..." : text;
      return (
        <g transform={`translate(${x},${y})`}>
          <text x={0} y={0} dy={16} textAnchor="end" fill="#475569" transform="rotate(-35)" fontSize={11} fontWeight={700}>
            {displayedText}
          </text>
        </g>
      );
    }

    const words = text.split(" ");
    const lines = [];
    let currentLine = "";
    words.forEach(word => {
      if ((currentLine + word).length > 20) {
        if (currentLine) lines.push(currentLine.trim());
        currentLine = word + " ";
      } else {
        currentLine += word + " ";
      }
    });
    if (currentLine) lines.push(currentLine.trim());

    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={16} textAnchor="end" fill="#475569" transform="rotate(-35)" fontSize={11}>
          {lines.map((line, i) => (
            <tspan key={i} x={0} dy={i === 0 ? 0 : "1.1em"}>{line}</tspan>
          ))}
        </text>
      </g>
    );
  };

  return (
    <Card
      sx={{
        height: open ? "85vh" : 460,
        minHeight: open ? "85vh" : 460,
        borderRadius: 4,
        boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        border: "1px solid #f1f5f9",
        display: "flex",
        flexDirection: "column",
        transition: "all 0.3s ease",
        "&:hover": { boxShadow: "0 8px 30px rgba(0,0,0,0.08)" },
      }}
    >
      <CardContent sx={{ p: 2, display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#475569", fontSize: "1rem", display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ p: 0.8, bgcolor: "#eef2ff", borderRadius: 2, display: "flex" }}>
              <PieChartOutlineIcon sx={{ color: "#6366f1", fontSize: "1.2rem" }} />
            </Box>
            Budget Vs Actuals
          </Typography>
          {!open && <IconButton onClick={() => setOpen((prev) => ({ ...prev, project: !prev.project }))}><OpenInFullIcon /></IconButton>}
        </Box>

        {open && (
          <Grid container spacing={1.5} mb={2}>
            <Grid item xs={12} sm={open ? 4 : 12}>
              <Autocomplete
                size="small"
                fullWidth
                value={peId || null}
                options={PeIDs || []}
                getOptionLabel={(option) => option?.["costcentre"] || ""}
                isOptionEqualToValue={(option, value) =>
                  option?.["IP Number"] === value?.["IP Number"]
                }
                onChange={(event, newValue) => setPeid(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Project"
                    variant="outlined"
                    name="PeId"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                )}
              />
            </Grid>
          </Grid>
        )}

        <Box sx={{ flexGrow: 1, minHeight: 0 }}>
          {loading ? (
            <Box sx={{ height: "100%", pt: 2 }}>
              <Skeleton variant="rectangular" width="100%" height={open ? 600 : 300} sx={{ borderRadius: 3 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2, px: 2 }}>
                {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} width={60} height={15} />)}
              </Box>
            </Box>
          ) : reports?.length > 0 ? (
            <Box
              sx={{
                width: "100%",
                height: open ? 700 : "100%",
                mt: 1,
                p: 1.5,
                border: "1px solid #f1f5f9",
                borderRadius: 3,
                bgcolor: "#fcfdfe",
                overflowX: "auto"
              }}
            >
              <ResponsiveContainer width="100%" height={open ? 600 : "100%"}>
                <BarChart
                  data={reports}
                  minWidth={Math.max(400, (reports?.length || 0) * 70)}
                  margin={{ top: 20, right: 30, left: 0, bottom: open ? 120 : 0 }}
                >
                  <CartesianGrid strokeDasharray="10 6" />

                  <XAxis
                    dataKey="name"
                    interval={0}
                    tick={<CustomizedAxisTick />}
                    height={open ? 120 : 90}
                  />

                  <YAxis
                    tick={{ fontSize: 10, fill: "#475569", fontWeight: 700 }}
                    tickFormatter={(v) =>
                      v >= 10000000
                        ? `${(v / 10000000).toFixed(1)} Cr`
                        : v >= 1000
                          ? `${(v / 1000).toFixed(1)}k`
                          : v
                    }
                  />

                  <Tooltip content={<CustomTooltip />} />
                  <Legend verticalAlign="top" height={36} />

                  <Bar
                    dataKey="Budget"
                    fill="#6366f1"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={40}
                  >
                    <LabelList
                      dataKey="Budget"
                      position="top"
                      formatter={(v) =>
                        v >= 10000000
                          ? `${(v / 10000000).toFixed(1)}Cr`
                          : v >= 1000
                            ? `${(v / 1000).toFixed(1)}k`
                            : v.toLocaleString("en-IN")
                      }
                      style={{ fontSize: 8, fill: "#475569", fontWeight: 500 }}
                    />
                  </Bar>
                  <Bar
                    dataKey="Actuals"
                    fill="#10b981"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={40}
                    minPointSize={6}
                  >
                    <LabelList
                      dataKey="Actuals"
                      position="top"
                      formatter={(v) =>
                        v >= 10000000
                          ? `${(v / 10000000).toFixed(1)}Cr`
                          : v >= 1000
                            ? `${(v / 1000).toFixed(1)}k`
                            : v.toLocaleString("en-IN")
                      }
                      style={{ fontSize: 9, fill: "#475569", fontWeight: 500 }}
                    />
                  </Bar>

                </BarChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "#94a3b8",
                border: "1px solid #f1f5f9",
                borderRadius: 3,
                p: 3,
                gap: 1
              }}
            >
              <PieChartOutlineIcon sx={{ fontSize: 40, opacity: 0.5 }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                No data found
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card >
  );
};

export default ProjectDashboard;
